import React from 'react';
import {useTranslation} from 'react-i18next';
import {
  getSearchParamFromUrl,
  useDebounce,
  useEvent,
  useTimeoutRef,
  Paginated,
  SelectOption,
} from '@chekin/core';
import {InfiniteScrollSelectProps} from '../InfiniteScrollSelect/InfiniteScrollSelect';
import {InfiniteData, useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import InfiniteScrollSelect from '../InfiniteScrollSelect';
import {SelectRefType} from '@guestapp/old-ui-kit';

const SEARCH_QUERY_DEBOUNCE_MS = 700;

function getConsumableOptions<T, V, L>(
  data: InfiniteData<Paginated<SelectOption<T, V, L>>>,
) {
  return data?.pages
    .map(data => {
      return data?.results || [];
    })
    .flat();
}

type QueriedInfiniteScrollSelectProps<T, V, L> = InfiniteScrollSelectProps<T, V, L> & {
  fetcher: (
    key: string,
    page: number,
    searchQuery: string,
  ) => Promise<Paginated<SelectOption<T, V, L>>>;
  queryKey: string | (string | undefined)[];
  optionsFilter?: (
    options: SelectOption<T, V, L>[],
    params: {searchQuery: string},
  ) => SelectOption<T, V, L>[];
  onOptionsChange?: (options: SelectOption<T, V, L>[]) => void;
  blockQuery?: boolean;
  minSearchQueryLength?: number;
  listAutoSizer?: boolean;
  mobileVersion?: boolean;
  onError?: (error: Error) => void;
};

const QueriedInfiniteScrollSelectInternal = <T, V, L>(
  {
    fetcher,
    queryKey,
    optionsFilter,
    onOptionsChange,
    blockQuery,
    onInputChange,
    minSearchQueryLength = 0,
    onError,
    ...props
  }: QueriedInfiniteScrollSelectProps<T, V, L>,
  ref: React.ForwardedRef<SelectRefType<T, V, L>>,
) => {
  const {i18n} = useTranslation();
  const {mobileVersion} = props;
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useDebounce(
    searchQuery,
    SEARCH_QUERY_DEBOUNCE_MS,
  );
  const isStartedTypingRef = React.useRef(false);
  const focusTimeoutRef = useTimeoutRef();
  const selectRef = React.useRef<SelectRefType<T, V, L>>(null);
  const mainQueryKey = Array.isArray(queryKey) ? queryKey[0] || '' : queryKey;
  const handleError = useEvent(onError);

  const isBlockedFetcher =
    blockQuery ||
    (minSearchQueryLength && debouncedSearchQuery.length < minSearchQueryLength);

  const fetchWithSearchQuery = React.useCallback(
    ({pageParam = 1}: {pageParam: unknown}) => {
      return fetcher(mainQueryKey, pageParam as number, debouncedSearchQuery);
    },
    [fetcher, mainQueryKey, debouncedSearchQuery],
  );

  const additionalDependencies = Array.isArray(queryKey) ? queryKey.slice(1) : [];
  const {data, error, status, hasNextPage, fetchNextPage, isFetchingNextPage} =
    useInfiniteQuery<Paginated<SelectOption<T, V, L>>, Error>({
      queryKey: [
        mainQueryKey,
        ...additionalDependencies,
        debouncedSearchQuery,
        i18n.language,
      ],
      queryFn: fetchWithSearchQuery,
      initialPageParam: 1,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: !isBlockedFetcher,
      getNextPageParam: lastGroup => {
        if (lastGroup?.next) {
          return Number(getSearchParamFromUrl('page', lastGroup.next));
        }
      },
    });

  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  React.useEffect(() => {
    if (data) {
      isStartedTypingRef.current = false;
    }
  }, [data]);

  React.useLayoutEffect(() => {
    if (isStartedTypingRef.current) {
      isStartedTypingRef.current = false;
    }
  }, [debouncedSearchQuery]);

  const options = React.useMemo(() => {
    if (!data) {
      return [];
    }

    let newOptions = getConsumableOptions(data);

    if (optionsFilter) {
      newOptions = optionsFilter(newOptions, {searchQuery});
    }

    onOptionsChange?.(newOptions);

    return newOptions;
  }, [data, onOptionsChange, optionsFilter, searchQuery]);

  React.useImperativeHandle(ref, () => selectRef.current as SelectRefType<T, V, L>);

  React.useEffect(
    function autoFocusAfterTimeout() {
      if (!mobileVersion) return;
      focusTimeoutRef.current = setTimeout(() => {
        selectRef.current?.focus();
        clearTimeout(focusTimeoutRef.current);
      }, 400);
    },
    [focusTimeoutRef, mobileVersion],
  );

  const handleLoadMore = () => {
    if (!isFetchingNextPage) return fetchNextPage();
  };

  const checkIfThereIsInCache = React.useCallback(
    (value: string) => {
      return !!queryClient.getQueryData([queryKey, value, i18n.language]);
    },
    [queryClient, queryKey, i18n.language],
  );

  const handleInputChange: QueriedInfiniteScrollSelectProps<T, V, L>['onInputChange'] = (
    value,
    meta,
  ) => {
    if (isFetchingNextPage || status === 'pending') return;
    if (searchQuery !== value) {
      onOptionsChange?.([]);
      onInputChange?.(value, meta);

      if (checkIfThereIsInCache(value)) {
        setDebouncedSearchQuery(value);
        isStartedTypingRef.current = false;
      } else {
        const isValueNotEqualDebounced = value !== debouncedSearchQuery;
        isStartedTypingRef.current = Boolean(value) && isValueNotEqualDebounced;
      }

      setSearchQuery(value);
    }
  };

  return (
    <InfiniteScrollSelect
      ref={selectRef}
      loading={isStartedTypingRef.current || (status === 'pending' && !options?.length)}
      onInputChange={handleInputChange}
      inputValue={searchQuery}
      canLoadMore={hasNextPage}
      loadMoreItems={handleLoadMore}
      isSearchable
      options={options}
      filterOption={null}
      {...props}
    />
  );
};

const QueriedInfiniteScrollSelect = React.forwardRef(
  QueriedInfiniteScrollSelectInternal,
) as <T, V, L>(
  props: QueriedInfiniteScrollSelectProps<T, V, L> & {
    ref?: React.ForwardedRef<SelectRefType<T, V, L>>;
  },
) => ReturnType<typeof QueriedInfiniteScrollSelectInternal>;

export {QueriedInfiniteScrollSelect};
export type {QueriedInfiniteScrollSelectProps};
